import z from 'zod';

import { isStructurallyValidIban } from '@dealstack/shared/lib/common/business/iban';

import { BANK_ACCOUNT_FORM_FIELDS } from '../../types';

// Every field is a plain string here; which of them are required depends on the others,
// so the rule lives in refineIbanFieldset. Bank name and the verified country code are
// lookup-derived and never required.
export const IbanFieldsetShape = z.object({
	[BANK_ACCOUNT_FORM_FIELDS.iban]: z.preprocess((v) => v ?? '', z.string()),
	[BANK_ACCOUNT_FORM_FIELDS.account_number]: z.preprocess((v) => v ?? '', z.string()),
	[BANK_ACCOUNT_FORM_FIELDS.bic]: z.preprocess((v) => v ?? '', z.string()),
	[BANK_ACCOUNT_FORM_FIELDS.bank_name]: z.preprocess((v) => v ?? '', z.string()),
	[BANK_ACCOUNT_FORM_FIELDS.verified_country_code]: z.preprocess((v) => v ?? '', z.string()),
});

type IbanFieldsetValues = {
	[BANK_ACCOUNT_FORM_FIELDS.iban]: string;
	[BANK_ACCOUNT_FORM_FIELDS.account_number]: string;
	[BANK_ACCOUNT_FORM_FIELDS.bic]: string;
};

/**
 * An IBAN, or the account number and SWIFT/BIC entered manually — countries with no entry
 * format of their own reach this fieldset, and plenty of accounts there have never had an
 * IBAN. Requiring one would leave them unsaveable.
 *
 * Applied after the shell extends this fragment: `.extend(shape)` copies fields, so an
 * object-level refinement declared here would be dropped.
 */
export const refineIbanFieldset = (values: IbanFieldsetValues, ctx: z.RefinementCtx): void => {
	const iban = values[BANK_ACCOUNT_FORM_FIELDS.iban].trim();
	const accountNumber = values[BANK_ACCOUNT_FORM_FIELDS.account_number].trim();
	const bic = values[BANK_ACCOUNT_FORM_FIELDS.bic].trim();

	if (iban) {
		if (!isStructurallyValidIban(iban)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Enter a valid IBAN',
				path: [BANK_ACCOUNT_FORM_FIELDS.iban],
			});
		}
		return;
	}

	// Nothing entered at all: ask for the IBAN, the path this form leads with. The manual
	// fields are only asked for once the entry has committed to them.
	if (!accountNumber && !bic) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: 'IBAN is required',
			path: [BANK_ACCOUNT_FORM_FIELDS.iban],
		});
		return;
	}

	if (!accountNumber) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: 'Account number is required without an IBAN',
			path: [BANK_ACCOUNT_FORM_FIELDS.account_number],
		});
	}

	if (!bic) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: 'SWIFT/BIC is required without an IBAN',
			path: [BANK_ACCOUNT_FORM_FIELDS.bic],
		});
	}
};
